/**
 * Add Manufacturer Form Composer
 *
 * Simple form with just the manufacturer name.
 * manufacturerNumber is auto-generated on submission.
 *
 * Form metadata and field configurations are loaded from form.yml
 */

import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  AddManufacturerFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddManufacturerFormData
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Factory } from 'lucide-react'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Component Props
// ============================================================================

export type AddManufacturerComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<AddManufacturerFormData>
  showHeader?: boolean
  submitLabel?: string
}

// ============================================================================
// Field validation via Zod schema (onBlur only)
// ============================================================================

const validateField = (name: keyof AddManufacturerFormData, value: unknown): string | null => {
  const shape = AddManufacturerFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// ============================================================================
// Composer Component
// ============================================================================

export function AddManufacturerComposer(props: AddManufacturerComposerProps) {
  const { onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  // Get field configurations from YAML
  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequiredField = (name: string) => {
    return name === 'manufacturerName'
  }

  const upsertManufacturer = useApiMutation('upsertDeviceManufacturer', 'POST', {
    invalidates: ['getDeviceManufacturers'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to create manufacturer:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: AddManufacturerFormSchema,
    defaultValues: {
      manufacturerName: initialValues?.manufacturerName ?? '',
    },
    onSubmit: async ({ value }) => {
      const formValue = value as AddManufacturerFormData
      // Validate with Zod schema (single source of truth)
      const parsed = AddManufacturerFormSchema.safeParse({
        manufacturerName: formValue.manufacturerName?.trim() ?? '',
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as any, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      // Transform and submit
      const apiRequest = transformToApiRequest(parsed.data)
      await upsertManufacturer.mutateAsync(apiRequest)
    },
  })

  // Render a single control with explicit render function
  const renderControl = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) => validateField(control.name as keyof AddManufacturerFormData, value) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Render form content with groups
  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []

      if (groupControls.length === 0) return null

      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  const formBody = (
    <>
      {renderFormContent()}

      <div className="pt-4 flex justify-center gap-3">
        {renderButtonControl({
          label: FORM_CONFIG.cancelButton?.label ?? '',
          variant: 'outline',
          onClick: onCancel,
          visibility: !!onCancel,
          type: 'button'
        })}

        {renderButtonControl({
          label: upsertManufacturer.isPending
            ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Submitting...')
            : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Submit'),
          variant: 'default',
          type: 'submit'
        }, { SubmitButton: form.SubmitButton })}
      </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Factory className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}

// Re-exports
export {
  AddManufacturerFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddManufacturerFormData
} from './schema'
